import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CircularProgress } from '@mui/material';

const CreateGroupChat = () => {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axiosInstance.get('/general/suggested');
        setAllUsers(res.data.users);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Reset search state when the query is cleared
  useEffect(() => {
    if (!searchQuery.trim()) {
      setHasSearched(false);
      setSearchResults([]);
      setError(null);
      setLoading(false);
    }
  }, [searchQuery]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const res = await axiosInstance.get(`/search/users/?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.users || []);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to fetch search results');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.length < 2) {
      alert('Please enter a group name and select at least two users.');
      return;
    }

    try {
      const res = await axiosInstance.post('/chat/create_group_chat', {
        name: groupName.trim(),
        members: selectedUsers
      });

      navigate(`/chat/${res.data.chat_id}`);
    } catch (err) {
      console.error('Failed to create group:', err);
    }
  };

  return (
    <div className="tailwind bg-white dark:bg-darkBackground min-h-screen text-black dark:text-white transition-colors max-w-xl mx-auto p-4 mt-8">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-chatBlueColor text-base"
        >
          Cancel
        </button>
        <h1 className="text-lg font-semibold">New Group</h1>
        <button
          onClick={handleCreateGroup}
					disabled={!groupName.trim() || selectedUsers.length < 2}
					className={`text-base font-medium ${
						!groupName.trim() || selectedUsers.length < 2
							? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
							: 'text-chatBlueColor'
					}`}
        >
          Create
        </button>
      </div>

      <div className="mb-4 border-b border-gray-200 dark:border-neutral-800 pb-2 flex items-center">
        <span className="text-gray-500 dark:text-gray-300 mr-2">Search:</span>
        <form onSubmit={handleSearch} className="flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border-none items-center focus:outline-none p-0 pt-[2px] bg-transparent text-black dark:text-white"
          />
        </form>
      </div>

			<div className="mb-4 pb-2 flex items-center">
				<input
					type="text"
					value={groupName}
					onChange={(e) => setGroupName(e.target.value)}
					placeholder="Enter a group name"
					className="flex-1 rounded-lg border border-transparent focus:outline-none p-2 text-base placeholder:text-gray-500 dark:placeholder-neutral-500 bg-gray-100 dark:bg-neutral-800 text-black dark:text-white"
				/>
			</div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-300 mb-3">
          {hasSearched ? 'Search results' : 'Select members'}
        </h2>
        <div className="divide-y divide-gray-200 dark:divide-neutral-800">
          {hasSearched ? (
            loading ? (
              <div className="text-center py-3 flex justify-center">
                <CircularProgress size={20} sx={{ color: '#d1d5db' }} />
              </div>
            ) : error ? (
              <div className="py-3 text-sm text-center text-red-500 dark:text-red-400">{error}</div>
            ) : searchResults.length > 0 ? (
              searchResults.map((user) => {
                const isSelected = selectedUsers.includes(user.user_id);
                return (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between cursor-pointer py-3 first:pt-0 last:pb-0"
                    onClick={() => toggleUserSelection(user.user_id)}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={user.profile_url || '/assets/images/default_profile.png'}
                        alt={user.username}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="text-gray-800 dark:text-white">{user.username}</span>
                    </div>
                    {isSelected ? (
                      <CheckCircleIcon className="w-6 h-6 text-chatBlueColor" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-gray-400 dark:border-gray-600" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="py-3 text-sm text-center text-gray-500 dark:text-gray-300">No results found</div>
            )
          ) : (
            allUsers.length > 0 ? (
              allUsers.map((user) => {
                const isSelected = selectedUsers.includes(user.user_id);
                return (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between cursor-pointer py-3 first:pt-0 last:pb-0"
                    onClick={() => toggleUserSelection(user.user_id)}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={user.profile_url || '/assets/images/default_profile.png'}
                        alt={user.username}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="text-gray-800 dark:text-white">{user.username}</span>
                    </div>
                    {isSelected ? (
                      <CheckCircleIcon className="w-6 h-6 text-chatBlueColor" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-gray-400 dark:border-gray-600" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="py-3 text-sm text-center text-gray-500 dark:text-gray-300">No suggestions available</div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGroupChat;